<script setup lang="ts">
  /**
   * FeedList — "For You" stream: section header with list/grid view toggle,
   * horizontal story rows with category eyebrow, native ad rows.
   */
  import { computed, ref, resolveComponent } from 'vue'
  import { resolveRegisteredComponent } from '../../registries/componentRegistry'
  import type { FeedItem, FeedListProps } from './types'

  const props = withDefaults(defineProps<FeedListProps>(), {
    heading: 'For You',
    items: () => [],
  })

  const view = ref<'list' | 'grid'>('list')
  const NuxtLink = resolveComponent('NuxtLink')

  /**
   * Both views open rows in a new tab and leave the portal untouched, the
   * same way the platform's own content navigation does
   * (useRedirectActions navigates with `{ open: { target: '_blank' } }`).
   * Sponsored rows keep their advertising rel tokens; story rows stay at
   * `noopener` rather than adding `noreferrer`, because they point at our
   * own same-origin article pages and those should still see the referrer.
   */
  function linkRel(item: FeedItem): string | undefined {
    if (!item.articleUrl) return undefined
    return item.isAd ? 'noopener noreferrer sponsored' : 'noopener'
  }

  /**
   * Per-category eyebrow colour, the way yahoo.com tints its river labels.
   * Measured there: five design-system tokens (link / red / rose / magenta
   * / lime) at scale 9, bucketed by domain, each with a darker value in
   * light and a lighter one in dark — e.g. Sports `#c62626` light and
   * `#e15c55` dark.
   *
   * Ours is the same idea at one hue per category instead of per bucket:
   * twelve hues 24° apart, every one at a single perceptual lightness and
   * chroma (OKLCH L 0.50 / C 0.13 for light, L 0.72 / C 0.13 for dark), so
   * they read as one family rather than twelve unrelated colours. The
   * violet/purple arc is skipped on purpose — this page carries no purple.
   *
   * Both values of every pair clear WCAG AA 4.5:1 on the card (#FEFEFE
   * light, #201F1F dark) and on the page (#F3F2EF / #100F0F). They land at
   * 5.1–6.4:1 light and 6.2–7.0:1 dark, at or under the neutral they
   * replaced, so the eyebrow stays quieter than the 14–17:1 headline.
   */
  const EYEBROW_COLOURS: Record<string, string> = {
    entertainment: 'text-[#963f71] dark:text-[#de82b6]', // magenta
    sports: 'text-[#9f3d4e] dark:text-[#e98092]', // red
    lifestyle: 'text-[#9f4327] dark:text-[#e98668]', // terracotta
    food: 'text-[#875809] dark:text-[#d29837]', // amber
    shopping: 'text-[#656909] dark:text-[#a9ac3f]', // gold
    politics: 'text-[#48710d] dark:text-[#8db456]', // olive
    automotive: 'text-[#347426] dark:text-[#77b868]', // leaf
    money: 'text-[#207633] dark:text-[#65bb77]', // green
    health: 'text-[#0b764d] dark:text-[#43be8e]', // emerald
    travel: 'text-[#0b7273] dark:text-[#1abbbd]', // teal
    technology: 'text-[#0a68a5] dark:text-[#4caeee]', // cyan
    news: 'text-[#475cad] dark:text-[#889ef6]', // blue
  }

  const EYEBROW_PALETTE = Object.values(EYEBROW_COLOURS)

  /**
   * The feed carries whatever category the news API attaches, not just the
   * nav's twelve, so anything unlisted (World, Science, US, …) takes a
   * stable slot in the same palette off a hash of its name. Deterministic:
   * a category always renders in the colour it rendered in last time.
   */
  function eyebrowColour(category: string): string {
    // Normalize once and hash the same value the lookup used, or "World",
    // "world" and "World " would each take a different palette slot
    const key = category.trim().toLowerCase()
    const known = EYEBROW_COLOURS[key]
    if (known) return known

    let hash = 0
    for (let i = 0; i < key.length; i++) {
      hash = (hash * 31 + key.charCodeAt(i)) >>> 0
    }
    return EYEBROW_PALETTE[hash % EYEBROW_PALETTE.length]
  }

  // Smart ad-row component contributed at runtime by the hosting app
  // (common registers PortalFeedAd at boot). Undefined in standalone
  // lego-land, where ad rows simply don't render.
  const PortalFeedAd = resolveRegisteredComponent('PortalFeedAd')

  /**
   * Ad rows carry no title or image of their own, so without the smart
   * component that fills them they would fall through to the story branch and
   * render an empty row. Standalone lego-land has no registry entry, so they
   * drop out of the list entirely, the same way HeroShowcase drops ad slides.
   */
  const rows = computed(() =>
    props.items.filter((item) => !item.adPlacement || PortalFeedAd)
  )
</script>

<template>
  <section data-portal-module="feed">
    <!-- Section header -->
    <div class="flex items-center justify-between py-2">
      <h2 class="text-xl font-bold text-content-primary">{{ heading }}</h2>
      <div class="flex items-center gap-2">
        <span class="text-xs text-content-secondary">Feed view</span>
        <div
          class="flex overflow-hidden rounded-md border border-[var(--yp-border)]"
        >
          <button
            type="button"
            aria-label="List view"
            :aria-pressed="view === 'list'"
            class="flex h-7 w-8 items-center justify-center transition-colors"
            :class="
              view === 'list'
                ? 'bg-[var(--yp-active)] text-content-primary'
                : 'text-content-tertiary hover:text-content-secondary'
            "
            @click="view = 'list'"
          >
            <UIcon name="i-heroicons-list-bullet" class="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Grid view"
            :aria-pressed="view === 'grid'"
            class="flex h-7 w-8 items-center justify-center transition-colors"
            :class="
              view === 'grid'
                ? 'bg-[var(--yp-active)] text-content-primary'
                : 'text-content-tertiary hover:text-content-secondary'
            "
            @click="view = 'grid'"
          >
            <UIcon name="i-heroicons-squares-2x2" class="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Stream: list rows share one card container, grid items are cards -->
    <div
      :class="
        view === 'grid'
          ? 'flex flex-col gap-3 pt-2 tablet:gap-2'
          : 'flex flex-col divide-y divide-[var(--yp-divider)] rounded-lg bg-surface-primary px-4'
      "
    >
      <template v-for="item in rows" :key="item.id">
        <!-- Native ad row: dplxapi ads-feed creative rendered exactly like
             the article rows around it (image, Ad chip, headline). Lazy-
             loads when the row nears the viewport; shows a row-shaped
             skeleton until the creative fills. -->
        <div
          v-if="item.adPlacement && PortalFeedAd"
          class="[&:not(:has(a))]:animate-pulse"
          :class="view === 'grid' ? 'rounded-lg bg-surface-primary p-4' : ''"
        >
          <component
            :is="PortalFeedAd"
            :placement="item.adPlacement"
            :view="view"
          />
        </div>

        <component
          :is="item.articleUrl ? NuxtLink : 'article'"
          v-else
          :to="item.articleUrl"
          :target="item.articleUrl ? '_blank' : undefined"
          :rel="linkRel(item)"
          class="group cursor-pointer"
          :class="
            view === 'list'
              ? 'flex items-start gap-3 py-4 tablet:gap-5'
              : 'block rounded-lg bg-surface-primary p-4'
          "
        >
          <!-- Card view: publisher header sits above the media, like Yahoo -->
          <p
            v-if="view === 'grid' && item.sourceName && !item.isAd"
            class="mb-3 flex items-center gap-2 text-sm font-bold text-content-primary"
          >
            <img
              v-if="item.sourceIconUrl"
              :src="item.sourceIconUrl"
              alt=""
              class="h-6 w-6 rounded-full"
            />
            {{ item.sourceName }}
          </p>

          <!-- Ratio lives on the wrapper so a broken image can't collapse the box -->
          <div
            class="relative aspect-[16/9] overflow-hidden rounded-lg"
            :class="view === 'list' ? 'w-[42%] max-w-[340px] shrink-0' : ''"
          >
            <!-- Card view: subtle image zoom on hover, like Yahoo -->
            <img
              :src="item.imageUrl"
              alt=""
              class="h-full w-full object-cover"
              :class="
                view === 'grid'
                  ? 'transition-transform duration-300 ease-out group-hover:scale-105'
                  : ''
              "
              loading="lazy"
            />
            <span
              v-if="view === 'grid' && item.category && !item.isAd"
              class="absolute bottom-3 left-3 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white"
            >
              {{ item.category }}
            </span>
          </div>

          <div class="min-w-0" :class="view === 'grid' ? 'mt-3' : ''">
            <!-- Ad label or category eyebrow -->
            <p
              v-if="item.isAd"
              class="flex items-center gap-1.5 text-xs text-content-secondary"
            >
              <span
                class="rounded-full border border-[var(--yp-chip-border)] px-2.5 py-0.5 text-[12px] font-bold text-content-primary"
              >
                Ad
              </span>
              <span class="text-[13px]">{{ item.adSource }}</span>
            </p>
            <p
              v-else-if="view === 'list' && item.category"
              class="text-xs font-bold"
              :class="eyebrowColour(item.category)"
            >
              {{ item.category }}
            </p>

            <!-- Title only, no summary snippet: measured on yahoo.com, their
                 river rows are 22px/28px semibold headlines with no
                 description. List rows underline on hover, cards zoom. -->
            <h3
              class="line-clamp-3 font-semibold text-content-primary"
              :class="
                view === 'list'
                  ? 'mt-1 text-base leading-snug group-hover:underline tablet:text-[22px] tablet:leading-7'
                  : 'text-xl leading-snug tablet:text-2xl'
              "
            >
              {{ item.title }}
            </h3>

            <p
              v-if="view === 'list' && item.sourceName"
              class="mt-2 flex items-center gap-1.5 text-xs text-content-secondary"
            >
              <img
                v-if="item.sourceIconUrl"
                :src="item.sourceIconUrl"
                alt=""
                class="h-3.5 w-3.5 rounded-sm"
              />
              {{ item.sourceName }}
            </p>
          </div>
        </component>
      </template>
    </div>
  </section>
</template>
